/**
 * ScheduleSlot Service
 * API integration for schedule slots CRUD operations
 *
 * @version 2025.1.0
 * @description Service for managing schedule slots (doctor availability)
 */

import apiClient from './apiClient';
import { ApiResponse } from '../types/common';
import {
  ScheduleSlot,
  ScheduleSlotListResponse,
  ScheduleSlotListParams,
  CreateScheduleSlotRequest,
  UpdateScheduleSlotRequest,
  OccupancyStats,
  SlotStatus,
} from '../types/scheduleSlot';

// Re-export types for convenience
export * from '../types/scheduleSlot';

/**
 * Helper to extract data from API response
 */
const extractData = <T>(response: ApiResponse<T>): T => {
  return response.data as T;
};

class ScheduleSlotService {
  private baseUrl = '/schedule-slots';

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Get paginated list of schedule slots
   */
  async getScheduleSlots(params: ScheduleSlotListParams = {}): Promise<ScheduleSlotListResponse> {
    const { page = 1, limit = 20, sortBy = 'slotDate', sortOrder = 'ASC', ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    // Add filters
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.clinicId) queryParams.append('clinicId', filters.clinicId.toString());
    if (filters.doctorName) queryParams.append('doctorName', filters.doctorName);
    if (filters.location) queryParams.append('location', filters.location);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.appointmentType) queryParams.append('appointmentType', filters.appointmentType);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.isRecurring !== undefined) queryParams.append('isRecurring', filters.isRecurring.toString());
    if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());

    // O cliente HTTP já devolve o corpo: { message, data: [...], pagination }.
    const response = await apiClient.get<ScheduleSlot[]>(`${this.baseUrl}?${queryParams.toString()}`);
    const pagination = response.pagination;

    return {
      scheduleSlots: response.data ?? [],
      total: pagination?.total ?? 0,
      page: pagination?.page ?? page,
      limit: pagination?.limit ?? limit,
      totalPages: pagination?.totalPages ?? 0,
    };
  }

  /**
   * Get schedule slot by ID
   */
  async getScheduleSlotById(id: number): Promise<ScheduleSlot> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return extractData(response);
  }

  /**
   * Create new schedule slot
   */
  async createScheduleSlot(data: CreateScheduleSlotRequest): Promise<ScheduleSlot> {
    console.log('📅 Creating schedule slot:', data);
    const response = await apiClient.post(this.baseUrl, data);
    console.log('✅ Schedule slot created successfully:', response.data);
    return extractData(response);
  }

  /**
   * Update schedule slot by ID
   */
  async updateScheduleSlot(id: number, data: UpdateScheduleSlotRequest): Promise<ScheduleSlot> {
    console.log('📅 Updating schedule slot:', { id, data });
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    console.log('✅ Schedule slot updated successfully:', response.data);
    return extractData(response);
  }

  /**
   * Delete schedule slot by ID (soft delete)
   */
  async deleteScheduleSlot(id: number): Promise<void> {
    console.log('❌ Deleting schedule slot:', id);
    await apiClient.delete(`${this.baseUrl}/${id}`);
    console.log('✅ Schedule slot deleted successfully');
  }

  // ============================================================================
  // SPECIALIZED QUERIES
  // ============================================================================

  /**
   * Get available slots for a specific date
   */
  async getAvailableSlots(
    date: string,
    filters?: { doctorName?: string; location?: string; companyId?: number; appointmentType?: string }
  ): Promise<ScheduleSlot[]> {
    const queryParams = new URLSearchParams({ date });

    if (filters?.doctorName) queryParams.append('doctorName', filters.doctorName);
    if (filters?.location) queryParams.append('location', filters.location);
    if (filters?.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters?.appointmentType) queryParams.append('appointmentType', filters.appointmentType);

    const response = await apiClient.get(`${this.baseUrl}/available?${queryParams.toString()}`);
    return extractData(response);
  }

  /**
   * Get dates that have available slots within a period
   */
  async getAvailableDates(
    startDate: string,
    endDate: string,
    companyId?: number
  ): Promise<string[]> {
    const queryParams = new URLSearchParams({ startDate, endDate });

    if (companyId) queryParams.append('companyId', companyId.toString());

    const response = await apiClient.get(`${this.baseUrl}/available-dates?${queryParams.toString()}`);
    return extractData(response);
  }

  /**
   * Get slots by doctor for a period
   */
  async getSlotsByDoctor(doctorName: string, startDate: string, endDate: string): Promise<ScheduleSlot[]> {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
    });

    const encodedDoctorName = encodeURIComponent(doctorName);
    const response = await apiClient.get(`${this.baseUrl}/doctor/${encodedDoctorName}?${queryParams.toString()}`);
    return extractData(response);
  }

  /**
   * Get slots by location for a date
   */
  async getSlotsByLocation(location: string, date: string): Promise<ScheduleSlot[]> {
    const queryParams = new URLSearchParams({ date });
    const encodedLocation = encodeURIComponent(location);
    const response = await apiClient.get(`${this.baseUrl}/location/${encodedLocation}?${queryParams.toString()}`);
    return extractData(response);
  }

  /**
   * Get occupancy statistics
   */
  async getStatistics(startDate: string, endDate: string, companyId?: number): Promise<OccupancyStats> {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
    });

    if (companyId) queryParams.append('companyId', companyId.toString());

    const response = await apiClient.get(`${this.baseUrl}/statistics?${queryParams.toString()}`);
    return extractData(response);
  }

  // ============================================================================
  // BLOCK/UNBLOCK OPERATIONS
  // ============================================================================

  /**
   * Block a slot
   */
  async blockSlot(id: number, reason?: string): Promise<ScheduleSlot> {
    console.log('🔒 Blocking slot:', { id, reason });
    const response = await apiClient.put(`${this.baseUrl}/${id}/block`, { reason });
    console.log('✅ Slot blocked successfully');
    return extractData(response);
  }

  /**
   * Unblock a slot
   */
  async unblockSlot(id: number): Promise<ScheduleSlot> {
    console.log('🔓 Unblocking slot:', id);
    const response = await apiClient.put(`${this.baseUrl}/${id}/unblock`);
    console.log('✅ Slot unblocked successfully');
    return extractData(response);
  }

  // ============================================================================
  // AVAILABILITY CHECK
  // ============================================================================

  /**
   * Check slot availability
   */
  async checkAvailability(id: number): Promise<{ exists: boolean; available: boolean; slot?: ScheduleSlot }> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/availability`);
    return extractData(response);
  }

  // ============================================================================
  // ENUM VALUES
  // ============================================================================

  /**
   * Get valid enum values from backend
   */
  async getEnums(): Promise<{
    appointmentTypes: string[];
    statuses: string[];
    recurringPatterns: string[];
  }> {
    const response = await apiClient.get(`${this.baseUrl}/enums`);
    return extractData(response);
  }

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; module: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Check if slot is available for booking
   */
  isAvailable(slot: ScheduleSlot): boolean {
    const availableStatuses = [SlotStatus.DISPONIVEL, SlotStatus.PARCIALMENTE_OCUPADO];
    return (
      slot.isActive &&
      availableStatuses.includes(slot.status) &&
      slot.currentBookings < slot.maxCapacity
    );
  }

  /**
   * Get remaining capacity
   */
  getRemainingCapacity(slot: ScheduleSlot): number {
    return Math.max(0, slot.maxCapacity - slot.currentBookings);
  }

  /**
   * Get occupancy percentage
   */
  getOccupancyPercentage(slot: ScheduleSlot): number {
    if (slot.maxCapacity === 0) return 0;
    return Math.round((slot.currentBookings / slot.maxCapacity) * 100);
  }

  /**
   * Format time range for display
   */
  formatTimeRange(slot: ScheduleSlot): string {
    return `${slot.startTime} - ${slot.endTime}`;
  }

  /**
   * Format capacity for display
   */
  formatCapacity(slot: ScheduleSlot): string {
    return `${slot.currentBookings}/${slot.maxCapacity}`;
  }

  /**
   * Format date for display (DD/MM/YYYY)
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';

    // Check if already in DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Convert from YYYY-MM-DD to DD/MM/YYYY
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  /**
   * Check if slot can be edited
   */
  canEdit(slot: ScheduleSlot): boolean {
    return slot.isActive && slot.status !== SlotStatus.CANCELADO;
  }

  /**
   * Check if slot can be deleted
   */
  canDelete(slot: ScheduleSlot): boolean {
    return slot.currentBookings === 0;
  }

  /**
   * Check if slot can be blocked
   */
  canBlock(slot: ScheduleSlot): boolean {
    return slot.isActive && slot.status !== SlotStatus.BLOQUEADO && slot.status !== SlotStatus.CANCELADO;
  }

  /**
   * Check if slot can be unblocked
   */
  canUnblock(slot: ScheduleSlot): boolean {
    return slot.status === SlotStatus.BLOQUEADO;
  }

  /**
   * Get status based on bookings
   */
  getStatusFromBookings(current: number, max: number): SlotStatus {
    if (current === 0) return SlotStatus.DISPONIVEL;
    if (current >= max) return SlotStatus.TOTALMENTE_OCUPADO;
    return SlotStatus.PARCIALMENTE_OCUPADO;
  }
}

// Export singleton instance
export const scheduleSlotService = new ScheduleSlotService();
export default scheduleSlotService;
